import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { invoke } from "./tauri-web";
import { listen } from "./tauri-web";
import { platform, isTauri } from "./tauri-web";
import { syncReminders } from "./reminders";
import { PhotoViewer } from "./PhotoViewer";
import { openFileDialog, saveFileDialog } from "./tauri-web";
import { readTextFile } from "@tauri-apps/plugin-fs";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac, openAccountDelete } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import {
  store, ALL_KINDS, KIND_LABELS, KIND_EMOJI, LABEL_TEXT, fmtDate, relativeDateCopy, urgency,
  todayLocal, type Document, type DocumentMeta, type DocKind, type KeyDate, type KeyDateLabel, type Passage, type HeadlineFact,
} from "./store";
import {
  askDocuments, openWebLogin, BrainError, AdviceBlockedError, type BrainErrorCode,
  type AskCitation,
} from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { BackupMenu } from "./BackupMenu";
import { buildExport } from "./export";
import Chat from "./chat/Chat";
import {
  Sparkles, CalendarClock, Trash2, Lock, FileText, WifiOff, RefreshCw, Plus, Search, ShieldCheck,
  Copy, Check, AlertTriangle, Clock, Wallet, KeyRound,
} from "lucide-react";

// SCREENSHOT=0 — sim-only auth/consent bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

// Design port 2026-08-26 — the deliverable's filing system: a two-letter archival tab on a
// kind-muted well (KIND_META in the deliverable) replaces the emoji/colour tiles. Palettes live
// in styles.css as --kt-* tokens (dark + light both designed).
const KIND_TAB: Record<DocKind, string> = {
  lease: "LE", insurance: "IN", warranty: "WA", id: "ID", utility: "UT", other: "OT",
};
function KindToken({ kind, size = 42 }: { kind: DocKind; size?: number }) {
  return (
    <span title={KIND_LABELS[kind]} className={`lbx-kt ${kind}`} style={{
      width: size, height: size, borderRadius: Math.round(size * 0.28),
      fontSize: Math.max(9, Math.round(size * 0.27)),
    }}>{KIND_TAB[kind]}</span>
  );
}

// The lockbox mark (deliverable's SVG glyph) — header tile + FAB, drawn in currentColor.
function LockboxMark({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="7" width="18" height="14" rx="3" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3 11h18" stroke="currentColor" strokeWidth="1.8" />
      <path d="M9 7V5a3 3 0 0 1 6 0v2" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2/§3, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors are ignored.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account, came back").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-lockbox://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to oriora-lockbox://deleted.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented && !SCREENSHOT) {
    return <AiConsent brand="Oriora Lockbox"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <BootScreen />;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

// ── Boot / splash (replaces a bare "Loading…") ──────────────────────────────────
function BootScreen() {
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "1.5rem" }}>
      <div style={{ display: "grid", placeItems: "center", gap: "0.75rem" }}>
        <span style={{ fontSize: "2rem" }}><Lock size={32} color="var(--accent)" /></span>
        <div className="dim" style={{ fontSize: "var(--text-sm)" }}>Opening your Lockbox…</div>
      </div>
    </main>
  );
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="hero" style={{ width: "100%", maxWidth: 400, textAlign: "center", padding: "2.25rem 2rem" }}>
        <div style={{ display: "inline-flex", padding: "0.85rem", borderRadius: "var(--radius-lg)", background: "var(--accent-soft)", marginBottom: "1rem" }}>
          <Lock size={28} color="var(--accent)" />
        </div>
        <div className="label" style={{ color: "var(--accent)", justifyContent: "center" }}>Oriora Lockbox</div>
        <h1 className="h1" style={{ margin: "0.5rem 0 0.6rem" }}>Your private life-admin box</h1>
        <p className="muted" style={{ fontSize: "var(--text-sm)", lineHeight: 1.6, marginBottom: "1.5rem" }}>
          {/* §7b — "your documents stay on this device" was FALSE: this app mounts <CloudBackup>,
              which relays the WHOLE document store through Oriora to the user's own cloud the moment
              they tap Back up. The passages half was already honest; the qualifier is what was
              missing. Wording follows the approved Study Companion form (non-retention, plus the
              "unless you back it up" carve-out) — the claim is RETENTION, never visibility. */}
          Sign in with your Oriora account. Your documents stay on this device unless you back them up;
          only the passages that match a question pass through Oriora, which doesn&apos;t retain them.
        </p>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try { const p = platform(); if (p === "ios") await signInIos("oriora-lockbox"); else if (p === "macos") await signInMac("oriora-lockbox"); else if (p === "web") window.location.href = "/login?next=/oriora-lockbox/"; else await openWebLogin(); }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? <><Spinner /> Signing in…</> : "Sign in with Oriora"}
        </button>
        <p className="caption" style={{ marginTop: "1rem", display: "inline-flex", gap: "0.35rem", alignItems: "center", justifyContent: "center" }}>
          <ShieldCheck size={12} color="var(--success)" /> Documents stay on your device
        </p>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [docs, setDocs] = useState<DocumentMeta[]>([]);
  const [keyDates, setKeyDates] = useState<KeyDate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<DocumentMeta | null>(null);
  // Read view — you can open the box; a search hit or citation opens it AT the matched passage
  // (design port 2026-08-26: the signature interaction — highlight is presentational only).
  const [openDoc, setOpenDoc] = useState<{ id: string; highlight?: string } | null>(null);
  const [kindFilter, setKindFilter] = useState<DocKind | null>(null);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — no existing screen
  // changes. onChanged → reload() refreshes documents + key dates after a chat-driven mutation.
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const askRef = useRef<HTMLDivElement>(null);
  const datesRef = useRef<HTMLDivElement>(null);

  const reload = useCallback(async () => {
    setLoadErr(false);
    try {
      const [d, k] = await Promise.all([store.listDocuments(), store.upcomingKeyDates(todayLocal(), 100)]);
      setDocs(d); setKeyDates(k);
    } catch {
      setLoadErr(true);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // On-device reminders: fire a local notification at 9am the day BEFORE each upcoming key date
  // ("Passport: expiry on 2026-08-01"). Purely factual (the date the indexer found in the user's
  // OWN document), never advice. Native only; best-effort — nothing leaves the device.
  useEffect(() => {
    if (SCREENSHOT) return; // no notification-permission prompt during identity screenshots
    if (platform() === "web") return;
    const reminders = keyDates
      .filter((kd) => /^\d{4}-\d{2}-\d{2}$/.test(kd.date))
      .map((kd) => {
        const [y, m, d] = kd.date.split("-").map(Number);
        return {
          key: kd.id,
          title: `Upcoming ${kd.label}`,
          body: `${kd.doc_title}: ${kd.label} on ${kd.date}`,
          at: new Date(y, m - 1, d - 1, 9, 0, 0),
        };
      });
    void syncReminders(reminders);
  }, [keyDates]);

  // Native-menu actions (Actions menu in the macOS menu bar): Add / Ask / Key dates.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "add": setShowAdd(true); break;
        case "ask":
          askRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          askRef.current?.querySelector<HTMLInputElement>("input")?.focus();
          break;
        case "dates": datesRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const soonest = keyDates[0] ?? null;

  const doExport = useCallback(() => {
    void (async () => {
      const all = await store.allKeyDates(1000);
      if (all.length === 0) return;
      const { csv, filename } = buildExport(all);
      if (!isTauri()) {
        // Browser: no native save dialog — a plain blob download (fleet pattern, CloudBackup).
        // Before 2026-08-27 this called the Tauri dialog plugin unguarded → unhandled TypeError
        // on web and a silently dead Export button (found by the 1b control walk).
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
        return;
      }
      const p = await saveFileDialog({ defaultPath: filename });
      if (p) await writeTextFile(p, csv);
    })().catch(() => { /* export is a convenience — never throw into the UI */ });
  }, []);

  async function doDelete(id: string) {
    setConfirmDelete(null);
    await store.deleteDocument(id);
    await reload();
  }

  return (
    <main style={{
      maxWidth: 720, margin: "0 auto",
      padding: "max(2rem, env(safe-area-inset-top)) max(1.5rem, env(safe-area-inset-right)) calc(5.5rem + env(safe-area-inset-bottom)) max(1.5rem, env(safe-area-inset-left))",
    }}>
      {offline && (
        <div role="status" className="banner banner-warning" style={{ marginBottom: "1rem" }}>
          <WifiOff size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Answering paused — your documents and key dates still work offline.</span>
        </div>
      )}

      <header className="row" style={{ justifyContent: "space-between", marginBottom: "1.5rem", alignItems: "flex-start" }}>
        <div className="row" style={{ gap: "0.6rem" }}>
          {/* Design port 2026-08-26: accent lockbox tile + Spectral wordmark (deliverable header). */}
          <span className="lbx-tile"><LockboxMark size={14} /></span>
          <div>
            <h1 className="lbx-wordmark">Oriora Lockbox</h1>
            <div className="caption">{docs.length} document{docs.length === 1 ? "" : "s"} · on this device</div>
          </div>
        </div>
        <div className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
          <BackupMenu
            appName="Lockbox" fileSlug="lockbox" appScheme="oriora-lockbox"
            hasData={docs.length > 0} onRestored={reload}
          />
          <AccountMenu email={email} appSlug="oriora-lockbox" appName="Oriora Lockbox" onSignedOut={onSignedOut} />
          <AddFundsMenu />
        </div>
      </header>

      {/* LEAD — the box's own field: free on-device search first, Ask as the visible paid escalation.
          Replaces the SoonestHero countdown (that hero belonged to oriora_renewals) and the
          byte-identical full-width "Add document" pill shared with 17 siblings. */}
      <div ref={askRef}>
        <SearchAsk docs={docs} keyDates={keyDates} offline={offline}
          onOpenDoc={(id, highlight) => setOpenDoc({ id, highlight })}
          onShowDates={() => datesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} />
      </div>

      {/* "Add to the box" — the deliverable's dashed intake row (same behavior: opens AddDocument). */}
      {docs.length > 0 && (
        <button type="button" className="lbx-add-row" style={{ marginTop: "0.85rem" }} onClick={() => setShowAdd(true)}>
          <span className="plus"><Plus size={15} /></span>
          <span>
            <span style={{ display: "block", fontSize: "var(--text-sm)", fontWeight: 600 }}>Add to the box</span>
            <span className="caption" style={{ display: "block", marginTop: 1 }}>Kept on this device unless you back it up · asking sends only the matching passages</span>
          </span>
        </button>
      )}

      <div className="row" style={{ justifyContent: "space-between", margin: "1.75rem 0 0.75rem", flexWrap: "wrap", gap: "0.4rem" }}>
        <h2 className="h3" style={{ margin: 0 }}>Your box</h2>
        <span className="caption row" style={{ gap: "0.3rem" }}><Lock size={11} /> on this device</span>
      </div>

      {loadErr ? (
        <div className="card-raised" style={{ padding: "1.75rem 1.5rem", textAlign: "center" }}>
          <AlertTriangle size={26} color="var(--danger)" />
          <div className="h3" style={{ margin: "0.5rem 0 0.25rem" }}>Couldn't load your box</div>
          <div className="muted" style={{ fontSize: "var(--text-sm)", marginBottom: "1rem" }}>Something went wrong reading your documents on this device.</div>
          <button className="btn" onClick={() => { setLoading(true); void reload(); }}><RefreshCw size={14} /> Retry</button>
        </div>
      ) : loading ? (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="card" style={{ padding: "0.85rem 1rem", display: "flex", alignItems: "center", gap: "0.85rem" }}>
              <div className="skeleton-line" style={{ width: 28, height: 28, borderRadius: "var(--radius-md)" }} />
              <div style={{ flex: 1, display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 6 }}>
                <div className="skeleton-line" style={{ width: "60%" }} />
                <div className="skeleton-line" style={{ width: "35%" }} />
              </div>
            </div>
          ))}
        </div>
      ) : docs.length === 0 ? (
        <EmptyShelf onAdd={() => setShowAdd(true)} />
      ) : (
        <>
          {/* Filed by kind — the schema's own life categories, not one flat list. */}
          {(() => {
            const kinds = ALL_KINDS.filter((k) => docs.some((d) => d.kind === k));
            if (kinds.length <= 1) return null;
            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.9rem" }}>
                <button type="button" className={`chip${kindFilter === null ? " selected" : ""}`} onClick={() => setKindFilter(null)}>All</button>
                {kinds.map((k) => (
                  <button key={k} type="button" className={`chip${kindFilter === k ? " selected" : ""}`} onClick={() => setKindFilter(k)}>{KIND_LABELS[k]}</button>
                ))}
              </div>
            );
          })()}
          {ALL_KINDS.filter((k) => (kindFilter ? k === kindFilter : true) && docs.some((d) => d.kind === k)).map((k) => (
            <section key={k} style={{ marginBottom: "1.25rem" }}>
              <div className="label row" style={{ marginBottom: "0.5rem", gap: "0.4rem" }}>
                {KIND_LABELS[k]}
                <span className="dim" style={{ fontWeight: 400 }}>· {docs.filter((d) => d.kind === k).length}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
                {docs.filter((d) => d.kind === k).map((d) => (
                  <DocTile key={d.id} d={d} soonest={keyDates.find((kd) => kd.doc_id === d.id) ?? null}
                    onOpen={() => setOpenDoc({ id: d.id })} onDelete={() => setConfirmDelete(d)} />
                ))}
              </div>
            </section>
          ))}
        </>
      )}

      {/* Key dates — DEMOTED below the box (they are DERIVED from the documents above; leading with
          them made Lockbox read as a re-skin of oriora_renewals). */}
      <div ref={datesRef} style={{ marginTop: "0.5rem" }}>
        <KeyDatesLedger dates={keyDates} onExport={doExport} loading={loading} />
      </div>

      {openDoc && (
        <DocReadView id={openDoc.id} highlight={openDoc.highlight} keyDates={keyDates} onClose={() => setOpenDoc(null)} />
      )}

      {showAdd && (
        <AddDocument onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); void reload(); }} />
      )}

      {confirmDelete && (
        <ModalShell title="Remove document?" onCancel={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--text-sm)", margin: "0 0 1.25rem", lineHeight: 1.6 }}>
            Remove “{confirmDelete.title}” from your Lockbox? Its text and any key dates leave too. This can’t be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn" onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => doDelete(confirmDelete.id)}><Trash2 size={14} /> Remove</button>
          </div>
        </ModalShell>
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          + slide-over live on top; no existing screen is altered. A chat-driven change reloads the box. */}
      {!chatOpen && (
        <button className="lbx-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant" title="Chat">
          <LockboxMark size={22} />
        </button>
      )}
      <aside className={`lbx-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="lbx-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to add, find, or ask</span>
          <button className="link-btn" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </main>
  );
}

function SoonestHero({ soonest, totalDocs, upcomingCount, loading }: { soonest: KeyDate | null; totalDocs: number; upcomingCount: number; loading: boolean }) {
  if (loading) {
    return (
      <div className="hero row" style={{ gap: "1rem" }}>
        <Clock size={36} color="var(--accent)" style={{ opacity: 0.5 }} />
        <div style={{ flex: 1, display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
          <div className="skeleton-line" style={{ width: "70%", height: 18 }} />
          <div className="skeleton-line" style={{ width: "45%" }} />
        </div>
      </div>
    );
  }
  return (
    <div className="hero row" style={{ gap: "1rem", alignItems: "center" }}>
      <span style={{ display: "inline-flex", flexShrink: 0 }}><CalendarClock size={36} color="var(--accent)" /></span>
      <div style={{ minWidth: 0 }}>
        {soonest ? (
          <>
            <div className="label" style={{ color: "var(--accent)" }}>{LABEL_TEXT[soonest.label]} {relativeDateCopy(soonest.date)}</div>
            <div className="h2 tnum" style={{ margin: "0.15rem 0 0.2rem", lineHeight: 1.15 }}>{fmtDate(soonest.date)}</div>
            <div className="muted" style={{ fontSize: "var(--text-sm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {KIND_EMOJI[soonest.kind]} {soonest.doc_title}
              {upcomingCount > 1 && <span className="dim"> · {upcomingCount} upcoming key dates</span>}
            </div>
          </>
        ) : (
          <>
            <div className="h3" style={{ margin: 0 }}>Your life-admin box</div>
            <div className="muted" style={{ fontSize: "var(--text-sm)", marginTop: 4 }}>
              {totalDocs === 0 ? "Add a document to start your key-dates ledger." : "No upcoming dates found yet in your documents."}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── READY STATE: the shelf, drawn before anything is on it ────────────────────────────────────
// The box is a SHELF OF DATED OBJECTS, and at zero none of that was visible: the empty state was a
// single "Add your first document" card, `KeyDatesLedger` returns null with no dates, and the two
// things that actually differ from the leader — the free on-device key-dates ledger and where the
// documents live — were a caption. The shelf's own filing kinds (`ALL_KINDS`) and the ledger's own
// labels (`LABEL_TEXT`) are constants; both can be drawn before a document exists.
//
// Every sentence below is bound to the code that implements it, not to the matrix:
//   · `derive_key_dates` / lockbox-web.ts labels a found date renewal | expiry | notice | start |
//     due — FIVE, and `start` is the one the docs forget.
//   · `askDocuments` sends `{ question, passages[{doc_id, title, kind, page, snippet}],
//     headline_facts[{text, tone}], now_ts }` — so the document TITLE and the derived fact lines
//     cross too, not only "the matched passages". The copy says so.
//   · Cloud backup DOES relay the whole vault, by design, to the customer's OWN store — so the
//     "never leaves" sentence is scoped to asking, never stated as an absolute.
function EmptyShelf({ onAdd }: { onAdd: () => void }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
      {ALL_KINDS.map((k) => (
        <div key={k} className="card" style={{ padding: "0.85rem 1rem", display: "flex", alignItems: "center", gap: "0.85rem", opacity: 0.72 }}>
          <span style={{ fontSize: "1.25rem", flexShrink: 0 }} aria-hidden>{KIND_EMOJI[k]}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>{KIND_LABELS[k]}</div>
            <div className="caption">nothing filed here yet</div>
          </div>
          <span className="dim tnum" style={{ fontSize: "var(--text-sm)" }}>—</span>
        </div>
      ))}

      <div className="card-raised" style={{ padding: "1.15rem 1.15rem", marginTop: "0.35rem" }}>
        <div className="label" style={{ marginBottom: "0.5rem" }}>Key dates — free, on this device</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.7rem" }}>
          {(["expiry", "renewal", "notice", "start", "due"] as KeyDateLabel[]).map((l) => (
            <span key={l} className="chip">{LABEL_TEXT[l]}</span>
          ))}
        </div>
        <div className="muted" style={{ fontSize: "var(--text-xs)", lineHeight: 1.6 }}>
          When a document goes in, Lockbox reads the dates out of its text here on the device and
          files them under those five words. It is plain pattern-matching and a sort — no model does
          the date maths, and nothing is billed for it. A date Lockbox can’t find, it doesn’t invent.
        </div>
      </div>

      <div className="card" style={{ padding: "1.15rem 1.15rem" }}>
        <div className="label" style={{ marginBottom: "0.5rem" }}>What leaves this device</div>
        <div className="muted" style={{ fontSize: "var(--text-xs)", lineHeight: 1.6 }}>
          {/* §7b 2026-09-25: was "free and never leave" — an absolute, and the first thing a skimmer
              reads, while Cloud Backup ships. The paragraph already disclosed backup in its LAST
              sentence ("Backing up is separate and deliberate…"), so the substance was honest; the
              opening clause was not. Qualified here so the promise is true where it is made. */}
          Storing, searching, opening and key dates are free, and they
          stay on this device unless you back it up. <strong>Asking</strong> is
          the one paid step, and it sends only your question, the passages that matched it (with their
          document title and page) and a few short fact lines Lockbox worked out here — never the whole
          document. Backing up is separate and deliberate: that sends your vault to your <em>own</em>{" "}
          GitHub or Drive, when you ask it to.
        </div>
      </div>

      <button className="btn btn-primary" style={{ marginTop: "0.35rem", justifyContent: "center" }} onClick={onAdd}>
        <Plus size={16} /> Add your first document
      </button>
      <div className="caption" style={{ textAlign: "center" }}>
        A lease, a policy, a warranty — paste the text or import a .txt / .md file.
      </div>
    </div>
  );
}

// ── The ONE AI feature: ask your documents (cited, never advice) ──
type AskErr = { code: BrainErrorCode | "advice" | "empty"; message: string };

function SearchAsk({ docs, keyDates, offline, onOpenDoc, onShowDates }: {
  docs: DocumentMeta[]; keyDates: KeyDate[]; offline: boolean;
  onOpenDoc: (id: string, highlight?: string) => void; onShowDates: () => void;
}) {
  const docCount = docs.length;
  const [q, setQ] = useState("");
  const [lastAction, setLastAction] = useState<"none" | "search" | "ask">("none");
  // Free, on-device FTS5 search — the always-present front door (was reachable only via the chat sheet).
  const [searchResults, setSearchResults] = useState<Passage[] | null>(null);
  const [searching, setSearching] = useState(false);
  // The one billed call — the visible escalation, never the default.
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<string | null>(null);
  const [citations, setCitations] = useState<AskCitation[]>([]);
  // Design port 2026-08-26 — presentational: the retrieved passages are kept so a tapped citation
  // can open its document AT the matching passage (the deliverable's signature interaction).
  const [askPassages, setAskPassages] = useState<Passage[]>([]);
  const [confidence, setConfidence] = useState(0);
  const [facts, setFacts] = useState<HeadlineFact[]>([]);
  const [err, setErr] = useState<AskErr | null>(null);
  const [copied, setCopied] = useState(false);

  const disabled = docCount === 0;

  async function runSearch(query = q) {
    const trimmed = query.trim();
    if (!trimmed || searching) return;
    setSearching(true); setLastAction("search"); setAnswer(null); setErr(null); setCitations([]); setFacts([]);
    try { setSearchResults(await store.searchPassages(trimmed, 20)); }
    catch { setSearchResults([]); }
    finally { setSearching(false); }
  }

  async function runAsk(question = q) {
    const trimmed = question.trim();
    if (!trimmed || busy) return;
    setBusy(true); setLastAction("ask"); setErr(null); setAnswer(null); setCitations([]); setConfidence(0); setFacts([]); setCopied(false); setSearchResults(null); setAskPassages([]);
    try {
      // 1) Local FTS5 retrieval (on-device) — the top matching passages.
      const passages: Passage[] = await store.searchPassages(trimmed, 12);
      setAskPassages(passages);
      // 2) Derived-signal slice (on-device, deterministic) for the matched docs — pre-ranked facts.
      let headlineFacts: HeadlineFact[] = [];
      if (passages.length > 0) {
        try { headlineFacts = await store.headlineFactsForDocs(passages.map((p) => p.doc_id), todayLocal(), 6); }
        catch { /* facts are an enhancement; never block the answer */ }
      }
      // 3) The single billed call — sends ONLY those passages + the question + the fact sentences.
      const r = await askDocuments(trimmed, passages, headlineFacts);
      if (!r.answer) {
        setErr({ code: "empty", message: "I couldn't find that in your documents." });
      } else {
        setAnswer(r.answer);
        setCitations(r.citations);
        setConfidence(r.confidence);
        setFacts(headlineFacts);
      }
    } catch (e) {
      if (e instanceof AdviceBlockedError) setErr({ code: "advice", message: e.message });
      else if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Something went wrong — please try again." });
    } finally { setBusy(false); }
  }

  function copyAnswer() {
    if (!answer) return;
    const cites = citations.map((c) => `${c.title}${c.page ? ` (p.${c.page})` : ""}`).join("; ");
    const text = cites ? `${answer}\n\nSources: ${cites}` : answer;
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div className="card" style={{ padding: "1.1rem 1.25rem", borderTop: "3px solid var(--accent)" }}>
      {/* Design port 2026-08-26 (deliverable Home): the field carries the promise ("Search your
          box"); the dates-ahead chip sits beside the eyebrow; Add moved to the dashed intake row. */}
      <div className="row" style={{ justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
        <div className="display-eyebrow">Search your box</div>
        {keyDates.length > 0 && (
          <button type="button" className="lbx-dates-chip" onClick={onShowDates} title="Jump to your key dates">
            <CalendarClock size={13} /> {keyDates.length} date{keyDates.length === 1 ? "" : "s"} ahead
          </button>
        )}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); void runSearch(); }}>
        <div className="lbx-search">
          <Search size={15} style={{ color: q.trim() ? "var(--accent)" : "var(--text-tertiary)", flexShrink: 0 }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            disabled={disabled}
            placeholder={disabled ? "Add a document first…" : "Search your box"}
            style={{ flex: 1 }}
            aria-label="Search your documents"
          />
          <button type="submit" aria-label="Search"
            className="btn-ghost" style={{ padding: "6px", color: "var(--accent)", flexShrink: 0 }}
            disabled={disabled || searching || !q.trim()}>
            {searching ? <Spinner /> : <Search size={16} />}
          </button>
        </div>
      </form>

      {/* The paid escalation — free search is the default; asking is a deliberate, visible step. */}
      {!disabled && (
        <>
          <div className="row" style={{ justifyContent: "space-between", marginTop: "0.6rem", gap: "0.5rem", flexWrap: "wrap" }}>
            <span className="caption row" style={{ gap: "0.35rem" }}>
              <ShieldCheck size={12} style={{ color: "var(--success)", flexShrink: 0 }} /> Free search stays on your device — never advice.
            </span>
            <button type="button" className="btn btn-primary" style={{ fontSize: "var(--text-xs)", padding: "0.45rem 0.9rem" }}
              disabled={busy || offline || !q.trim()} onClick={() => void runAsk()}
              title={offline ? "Offline — answering paused" : "Ask this as a question (one billed AI call)"}>
              {busy ? <><Spinner /> Asking…</> : <><Sparkles size={13} /> Ask this as a question</>}
            </button>
          </div>
          {/* The per-question price line (deliverable caption under the Ask CTA). */}
          {q.trim() && !busy && (
            <div className="caption" style={{ textAlign: "right", marginTop: "0.35rem" }}>Uses one billed AI call.</div>
          )}
        </>
      )}

      {/* SEARCH results — passages that open the document they came from. */}
      {searching && (
        <div className="result-card" style={{ marginTop: "0.85rem", padding: "1rem", display: "grid", gap: 8 }}>
          <div className="skeleton-line" style={{ width: "80%" }} />
          <div className="skeleton-line" style={{ width: "60%" }} />
        </div>
      )}
      {lastAction === "search" && !searching && searchResults && (
        searchResults.length === 0 ? (
          <div className="banner" style={{ marginTop: "0.85rem", background: "var(--bg-muted)", border: "1px solid var(--border-hairline)" }}>
            <Search size={15} style={{ color: "var(--text-tertiary)", flexShrink: 0, marginTop: 1 }} />
            <div className="muted" style={{ fontSize: "var(--text-xs)", lineHeight: 1.5 }}>No matches in your box. Try different words, or “Ask this as a question”.</div>
          </div>
        ) : (
          <div style={{ marginTop: "0.85rem", display: "grid", gap: "0.5rem" }}>
            {/* Deliverable kicker: the free layer named where it runs. */}
            <div className="caption" style={{ textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
              On this device · free — {searchResults.length} passage{searchResults.length === 1 ? "" : "s"}
            </div>
            {searchResults.map((p, i) => (
              <button key={`${p.doc_id}-${i}`} type="button" className="card" onClick={() => onOpenDoc(p.doc_id, p.snippet)}
                title="Open the document at this passage"
                style={{ padding: "0.8rem 0.85rem", textAlign: "left", cursor: "pointer", display: "flex", gap: "0.7rem", alignItems: "flex-start", width: "100%" }}>
                <KindToken kind={p.kind} size={32} />
                <span style={{ flex: 1, minWidth: 0, display: "grid", gap: "0.2rem" }}>
                  <span className="row" style={{ justifyContent: "space-between", gap: "0.5rem" }}>
                    <span style={{ fontWeight: 600, fontSize: "var(--text-sm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</span>
                    <span className="dim" style={{ fontSize: 10, flexShrink: 0 }}>p.{p.page}</span>
                  </span>
                  {/* The document's own words in the serif voice (deliverable snippet treatment). */}
                  <span className="lbx-snippet" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.snippet}</span>
                </span>
              </button>
            ))}
          </div>
        )
      )}

      {lastAction === "ask" && busy && (
        <div aria-live="polite" className="result-card" style={{ marginTop: "0.85rem", padding: "1rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 10 }}>
          <span className="dim row" style={{ fontSize: "var(--text-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} /> Reading the matching passages…
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "88%" }} />
          <div className="skeleton-line" style={{ width: "64%" }} />
        </div>
      )}

      {lastAction === "ask" && err && !busy && <AskError err={err} onRetry={() => runAsk()} />}

      {lastAction === "ask" && answer && !busy && (
        <div className="result-card" style={{ marginTop: "0.85rem", padding: "1rem 1.1rem" }} aria-live="polite">
          <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.5rem" }}>
            <span className="label" style={{ color: "var(--text-tertiary)" }}>Answer</span>
            <button className="btn-ghost" style={{ padding: "0.25rem 0.5rem", fontSize: "var(--text-xs)" }} onClick={copyAnswer} aria-live="polite">
              {copied ? <><Check size={13} color="var(--success)" /> Copied</> : <><Copy size={13} /> Copy</>}
            </button>
          </div>
          <p style={{ fontSize: "var(--text-base)", color: "var(--text-primary)", lineHeight: 1.6, margin: 0 }}>{answer}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>

          {/* THE SIGNATURE (deliverable): a citation opens the document AT the passage that
              grounded it — matched via the retrieved passages kept in askPassages. */}
          {citations.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", marginTop: "0.7rem" }}>
              {citations.map((c, i) => {
                const src = askPassages.find((p) => p.doc_id === c.doc_id && p.page === c.page)
                  ?? askPassages.find((p) => p.doc_id === c.doc_id);
                return (
                  <button key={`${c.doc_id}-${i}`} type="button" className="lbx-citechip"
                    title={c.why ? `${c.why} — open at the passage` : "Open at the passage"}
                    onClick={() => onOpenDoc(c.doc_id, src?.snippet)}>
                    <FileText size={11} style={{ flexShrink: 0 }} />
                    <span className="txt">{c.title}{c.page ? ` · p.${c.page}` : ""}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Derived-signal slice surfaced: the on-device facts that grounded the answer. */}
          {facts.length > 0 && (
            <div style={{ marginTop: "0.85rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border-hairline)" }}>
              <div className="caption row" style={{ gap: "0.3rem", marginBottom: "0.4rem" }}>
                <Sparkles size={11} color="var(--accent)" /> From your documents
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.3rem" }}>
                {facts.slice(0, 4).map((f, i) => (
                  <div key={i} className="row" style={{ gap: "0.45rem", alignItems: "flex-start" }}>
                    <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, flexShrink: 0, marginTop: 6,
                      background: f.tone === "urgent" ? "var(--danger)" : f.tone === "notable" ? "var(--warning)" : "var(--accent)" }} />
                    <span className="muted" style={{ fontSize: "var(--text-xs)", lineHeight: 1.5 }}>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {confidence > 0 && (
            <div style={{ marginTop: "0.85rem" }}>
              <div className="row" style={{ justifyContent: "space-between", marginBottom: 4 }}>
                <span className="caption">Grounding confidence</span>
                <span className="caption tnum">{Math.round(confidence * 100)}%</span>
              </div>
              <div className="conf-track"><div className="conf-fill" style={{ width: `${Math.round(confidence * 100)}%` }} /></div>
              {confidence < 0.4 && (
                <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>Low confidence — this may not be clearly stated in your documents.</div>
              )}
            </div>
          )}

          <div style={{ marginTop: "0.85rem" }}>
            <span className="dim" style={{ fontSize: 10 }}>Quotes your documents — not legal, insurance or financial advice.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function AskError({ err, onRetry }: { err: AskErr; onRetry: () => void }) {
  // The LEGAL fence — advice question blocked before any AI call.
  if (err.code === "advice") {
    return (
      <div className="banner banner-success" style={{ marginTop: "0.85rem" }}>
        <ShieldCheck size={16} style={{ color: "var(--success)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--text-xs)", lineHeight: 1.5 }}>{err.message}</div>
      </div>
    );
  }
  // Insufficient funds — point to the Oriora account, NO amount shown.
  if (err.code === "insufficient_funds") {
    return (
      <div className="banner banner-accent" style={{ marginTop: "0.85rem" }}>
        <Wallet size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>Add funds to keep asking</div>
          <div className="muted" style={{ fontSize: "var(--text-xs)", marginTop: 2 }}>Add funds in your Oriora account, then ask again.</div>
        </div>
      </div>
    );
  }
  // BYOK gate — Managed AI blocked for this app; needs a vendor key.
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="banner banner-info" style={{ marginTop: "0.85rem" }}>
        <KeyRound size={16} style={{ color: "var(--info)", flexShrink: 0, marginTop: 1 }} />
        <div className="muted" style={{ fontSize: "var(--text-xs)" }}>{err.message}</div>
      </div>
    );
  }
  // Empty result — gentle, no retry (more/different words is the fix).
  if (err.code === "empty") {
    return (
      <div className="banner" style={{ marginTop: "0.85rem", background: "var(--bg-muted)", border: "1px solid var(--border-hairline)" }}>
        <Search size={15} style={{ color: "var(--text-tertiary)", flexShrink: 0, marginTop: 1 }} />
        <div className="muted" style={{ fontSize: "var(--text-xs)", lineHeight: 1.5 }}>{err.message} Try different words, or add the document that would have it.</div>
      </div>
    );
  }
  // Network / session / unavailable — recoverable, offer a retry.
  const retryable = err.code === "unavailable";
  return (
    <div className="banner banner-danger" style={{ marginTop: "0.85rem", justifyContent: "space-between", alignItems: "center" }}>
      <span className="row" style={{ gap: "0.45rem" }}>
        <AlertTriangle size={15} style={{ color: "var(--danger)", flexShrink: 0 }} />
        <span style={{ fontSize: "var(--text-xs)" }}>{err.message}</span>
      </span>
      {retryable && (
        <button className="btn" style={{ fontSize: "var(--text-xs)", padding: "0.3rem 0.6rem", flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

function KeyDatesLedger({ dates, onExport, loading }: { dates: KeyDate[]; onExport: () => void; loading: boolean }) {
  if (loading) return null;
  if (dates.length === 0) return null;
  return (
    <div className="card" style={{ padding: "1.1rem 1.25rem" }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.85rem", gap: "0.4rem" }}>
        <div className="row" style={{ gap: "0.4rem" }}>
          <CalendarClock size={16} style={{ color: "var(--accent)" }} />
          <div style={{ fontWeight: 600, fontSize: "var(--text-sm)" }}>Key dates</div>
          <span className="dim" style={{ fontSize: "var(--text-xs)" }}>· from your documents</span>
        </div>
        <button className="btn-ghost" style={{ fontSize: "var(--text-xs)", padding: "0.3rem 0.55rem" }} onClick={onExport}>Export CSV</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.45rem" }}>
        {/* Design port 2026-08-26 (deliverable ledger): serif date column left, kind tab right.
            The date keeps its urgency colour (semantic --due-* scale merged into the layout). */}
        {dates.slice(0, 12).map((k) => (
          <div key={k.id} className="kd-row">
            <span className="lbx-ledgerdate">
              <span className="md tnum" style={{ display: "block", color: `var(--due-${urgency(k.date)}, var(--text-primary))` }}>{fmtDate(k.date).replace(/ \d{4}$/, "")}</span>
              <span className="y tnum" style={{ display: "block" }}>{k.date.slice(0, 4)}</span>
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: "var(--text-sm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {LABEL_TEXT[k.label]}
              </div>
              <div className="dim" style={{ fontSize: "var(--text-xs)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{k.doc_title} · {relativeDateCopy(k.date)}</div>
            </div>
            <KindToken kind={k.kind} size={30} />
          </div>
        ))}
      </div>
      {dates.length > 12 && <div className="dim" style={{ fontSize: "var(--text-xs)", marginTop: 10 }}>+{dates.length - 12} more — export to see all</div>}
    </div>
  );
}

function DocTile({ d, soonest, onOpen, onDelete }: { d: DocumentMeta; soonest: KeyDate | null; onOpen: () => void; onDelete: () => void }) {
  const [zoom, setZoom] = useState(false);
  return (
    <>
      {/* The tile now OPENS the document (was previously un-openable — store.getDocument was dead code).
          Design port 2026-08-26: the document is a DATED OBJECT — its soonest key date is worn on
          the tile itself (deliverable subLine), falling back to the kind label. */}
      <div className="card" role="button" tabIndex={0} onClick={onOpen}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
        style={{ padding: "0.85rem 1rem", display: "flex", alignItems: "center", gap: "0.85rem", cursor: "pointer" }}>
        <KindToken kind={d.kind} size={42} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: "var(--text-sm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.title}</div>
          <div className="dim" style={{ fontSize: "var(--text-xs)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {soonest
              ? <><span style={{ color: `var(--due-${urgency(soonest.date)}, var(--text-tertiary))`, fontWeight: 600 }}>{LABEL_TEXT[soonest.label]} {fmtDate(soonest.date)}</span>{d.key_date_count > 1 && ` · +${d.key_date_count - 1} more`}</>
              : KIND_LABELS[d.kind]}
            {d.photo && " · 📷 photo"}
          </div>
        </div>
        {/* Photo attached on-device (via chat) — a thumbnail with tap-to-zoom. Never sent to the model. */}
        {d.photo && (
          <img src={d.photo} alt={`Photo attached to ${d.title}`} title="Tap to view"
            onClick={(e) => { e.stopPropagation(); setZoom(true); }}
            style={{ width: 40, height: 40, objectFit: "cover", borderRadius: "var(--radius-sm, 8px)", cursor: "zoom-in", flexShrink: 0 }} />
        )}
        <button className="btn-ghost" title="Remove document" aria-label="Remove document"
          style={{ padding: "0.4rem", color: "var(--text-tertiary)" }}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}>
          <Trash2 size={15} />
        </button>
      </div>
      {zoom && d.photo && (
        <PhotoViewer photos={[{ src: d.photo, alt: `Photo attached to ${d.title}` }]} onClose={() => setZoom(false)} />
      )}
    </>
  );
}

// Read view — you can finally OPEN the box: the stored text, its kind, and the key dates derived
// from it. Opened from a DocTile, a search result, or a citation under an answer.
// Design port 2026-08-26 — THE SIGNATURE SURFACE: the body renders as passage blocks in the serif
// document voice, and when opened from a search hit or citation the matching passage is
// highlighted in place ("Matched passage") and scrolled into view. Presentational only.

/** Split a document body into passage blocks (blank-line paragraphs), capped so a 400k-char body
    can't render thousands of nodes — the tail collapses into one final block. */
function splitPassages(body: string): string[] {
  const parts = body.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) return [body];
  if (parts.length > 400) return [...parts.slice(0, 399), parts.slice(399).join("\n\n")];
  return parts;
}
const normText = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();
/** Indices of the passage blocks the highlight snippet covers (empty = none). The snippet is the
    retrieval CHUNK (~1000 chars, can span several paragraphs and start/end mid-paragraph), so a
    block is matched when its head or tail appears in the chunk, or the chunk sits inside it. */
function findHighlight(blocks: string[], highlight?: string): number[] {
  if (!highlight) return [];
  const hay = normText(highlight.replace(/[…]/g, " "));
  if (hay.length < 8) return [];
  const hayProbe = hay.length > 80 ? hay.slice(0, 80) : hay;
  const out: number[] = [];
  blocks.forEach((b, i) => {
    const nb = normText(b);
    if (nb.length < 8) return;
    const head = nb.length > 80 ? nb.slice(0, 80) : nb;
    const tail = nb.length > 80 ? nb.slice(-80) : nb;
    if (hay.includes(head) || hay.includes(tail) || nb.includes(hayProbe)) out.push(i);
  });
  return out;
}

function DocReadView({ id, highlight, keyDates, onClose }: { id: string; highlight?: string; keyDates: KeyDate[]; onClose: () => void }) {
  const [doc, setDoc] = useState<Document | null | undefined>(undefined); // undefined = loading
  useEffect(() => {
    let alive = true;
    store.getDocument(id).then((d) => { if (alive) setDoc(d); }).catch(() => { if (alive) setDoc(null); });
    return () => { alive = false; };
  }, [id]);
  const dates = useMemo(() => keyDates.filter((k) => k.doc_id === id), [keyDates, id]);
  const blocks = useMemo(() => (doc ? splitPassages(doc.body) : []), [doc]);
  const hlSet = useMemo(() => findHighlight(blocks, highlight), [blocks, highlight]);
  const firstHl = hlSet.length > 0 ? hlSet[0] : -1;
  const hlRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (doc && firstHl >= 0) hlRef.current?.scrollIntoView({ block: "center" });
  }, [doc, firstHl]);

  return (
    <ModalShell title={doc && doc !== undefined ? doc.title : "Document"} onCancel={onClose} wide>
      {doc === undefined ? (
        <div style={{ display: "grid", gap: 8 }}>
          <div className="skeleton-line" style={{ width: "50%" }} />
          <div className="skeleton-line" style={{ width: "90%" }} />
          <div className="skeleton-line" style={{ width: "80%" }} />
        </div>
      ) : doc === null ? (
        <div className="muted" style={{ fontSize: "var(--text-sm)" }}>This document couldn't be opened.</div>
      ) : (
        <>
          <div className="row" style={{ gap: "0.6rem", marginBottom: "0.85rem" }}>
            <KindToken kind={doc.kind} size={34} />
            <div className="dim" style={{ fontSize: "var(--text-xs)" }}>
              {KIND_LABELS[doc.kind]} · {doc.char_count.toLocaleString()} chars · on this device
            </div>
          </div>
          {dates.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", marginBottom: "0.85rem" }}>
              {dates.map((k) => (
                <span key={k.id} className={`kd-pill ${urgency(k.date)} tnum`} title={k.context}>
                  {LABEL_TEXT[k.label]} · {fmtDate(k.date)}
                </span>
              ))}
            </div>
          )}
          <div style={{ fontWeight: 600, fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-tertiary)", marginBottom: "0.4rem" }}>Document text · on this device</div>
          <div style={{ maxHeight: "52vh", overflow: "auto", background: "var(--bg-app)", border: "1px solid var(--border-hairline)", borderRadius: "var(--radius-md)", padding: "0.35rem 0.35rem" }}>
            {blocks.map((b, i) => (
              <div key={i} ref={i === firstHl ? hlRef : undefined} className={`lbx-passage${hlSet.includes(i) ? " hl" : ""}`}>
                {i === firstHl && <div className="lbx-passage-label">Matched passage</div>}
                <div className="lbx-doc-text" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{b}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </ModalShell>
  );
}

const MAX_BODY_CHARS = 400_000;

function AddDocument({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<DocKind>("lease");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const charCount = useMemo(() => body.length, [body]);
  const nearLimit = charCount > MAX_BODY_CHARS * 0.9;

  async function importFile() {
    setErr(null);
    if (!isTauri()) {
      // Browser: the Tauri dialog plugin doesn't exist — use a real <input type=file> (fleet
      // pattern). Before 2026-08-27 the unguarded plugin call made web import a dead feature.
      const inp = document.createElement("input");
      inp.type = "file";
      inp.accept = ".txt,.text,.md,.markdown,.csv,.log,text/plain";
      inp.onchange = () => {
        const f = inp.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => {
          setBody(String(reader.result ?? "").slice(0, MAX_BODY_CHARS));
          if (!title.trim()) setTitle(f.name.replace(/\.[^.]+$/, ""));
        };
        reader.onerror = () => setErr("Could not read that file — paste the text instead.");
        reader.readAsText(f);
      };
      inp.click();
      return;
    }
    try {
      // v1 imports the TEXT of a .txt/.md/.csv file. OCR-from-photo + PDF parsing is a documented TODO.
      const picked = await openFileDialog({
        multiple: false,
        filters: [{ name: "Text documents", extensions: ["txt", "text", "md", "markdown", "csv", "log"] }],
      });
      const path = Array.isArray(picked) ? picked[0] : picked;
      if (!path) return;
      const text = await readTextFile(path as string);
      setBody(text.slice(0, MAX_BODY_CHARS));
      if (!title.trim()) {
        const base = (path as string).split(/[\\/]/).pop() ?? "Document";
        setTitle(base.replace(/\.[^.]+$/, ""));
      }
    } catch (e) {
      setErr(`Could not read that file — paste the text instead. (${e instanceof Error ? e.message : String(e)})`);
    }
  }

  async function save() {
    if (!body.trim()) { setErr("Add the document text — paste it or import a text file."); return; }
    setSaving(true); setErr(null);
    try {
      await store.addDocument({ title: title.trim() || "Untitled document", kind, body: body.slice(0, MAX_BODY_CHARS) });
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <ModalShell title="Add a document" onCancel={onClose} wide>
      <div className="label" style={{ marginBottom: "0.5rem" }}>What kind?</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "1.25rem" }}>
        {ALL_KINDS.map((k) => (
          <button key={k} type="button" className={`kind-rung${kind === k ? " selected" : ""}`} onClick={() => setKind(k)} aria-pressed={kind === k}>
            <span className="emoji">{KIND_EMOJI[k]}</span>
            <span className="lbl">{KIND_LABELS[k]}</span>
          </button>
        ))}
      </div>

      <div className="label" style={{ marginBottom: "0.5rem" }}>Title</div>
      <input placeholder="e.g. Flat tenancy agreement" value={title} onChange={(e) => setTitle(e.target.value)} style={{ marginBottom: "1.25rem" }} />

      <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.5rem" }}>
        <span className="label">Document text</span>
        <button type="button" className="btn-ghost" style={{ fontSize: "var(--text-xs)", padding: "0.3rem 0.6rem" }} onClick={importFile}>Import text file…</button>
      </div>
      <textarea rows={8} placeholder="Paste the document text here, or import a .txt / .md file. It's stored on this device unless you back it up." value={body} onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY_CHARS))} style={{ marginBottom: "0.4rem", minHeight: 160, resize: "vertical" }} />
      <div className="caption" style={{ marginBottom: "1.25rem", color: nearLimit ? "var(--warning)" : undefined }}>
        {body ? <span className="tnum">{charCount.toLocaleString()} characters · stored on this device unless you back it up</span>
              : "Photo / PDF import with on-device text recognition is coming. For now, paste the text or import a text file."}
      </div>

      {err && (
        <div className="banner banner-danger" style={{ marginBottom: "1rem" }}>
          <AlertTriangle size={15} style={{ color: "var(--danger)", flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: "var(--text-xs)", lineHeight: 1.5 }}>{err}</span>
        </div>
      )}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={!body.trim() || saving}>{saving ? <><Spinner /> Saving…</> : "Add to Lockbox"}</button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, children, onCancel, wide }: { title: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  // Esc closes; backdrop click closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "var(--bg-overlay)", backdropFilter: "blur(2px)", display: "grid", placeItems: "center", zIndex: 50, padding: "1rem" }}>
      <div className="card-raised" onClick={(e) => e.stopPropagation()}
        // FILL-THEN-CAP. width:480 + maxWidth:"100%" resolves the 100% against the centred grid
        // track, not the viewport, so the card rendered at full width on a 402pt phone with the
        // right-hand controls off-screen (Round Card, 2026-07-24).
        style={{ width: "100%", maxWidth: wide ? 480 : 360, padding: "1.5rem", boxShadow: "var(--shadow-lg)", maxHeight: "90vh", overflowY: "auto" }}>
        <div className="h3" style={{ marginBottom: "1.25rem" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

// ── Tiny inline spinner (button "Working…" state, §7.2) ─────────────────────────
function Spinner() {
  return (
    <span aria-hidden style={{
      width: 13, height: 13, borderRadius: 999, display: "inline-block",
      border: "2px solid currentColor", borderTopColor: "transparent",
      animation: "spin 0.7s linear infinite", verticalAlign: "-1px", marginRight: 2,
    }} />
  );
}
